/**
 * Chart Color Palettes
 *
 * Multi-color palettes for assigning distinct colors to different data entities.
 * Each palette contains vibrant, distinguishable colors suitable for:
 * - Bar charts (different bars get different colors)
 * - Line charts (different lines get different colors)
 * - Area charts (different areas get different colors)
 *
 * Usage:
 * ```ts
 * import { chartPalettes, defaultPalette } from '@ui-blocks/chartPalette';
 *
 * // Use the default palette (violet)
 * const colors = defaultPalette;
 *
 * // Or get a specific palette
 * const colors = chartPalettes.vibrant;
 *
 * // Use colors by index for different data entities
 * const color1 = colors[0];
 * const color2 = colors[1];
 * ```
 */

/**
 * Violet palette - elegant purple/violet tones (DEFAULT)
 * Modern, clean aesthetic with varying opacity levels for emphasis
 * Primary color: #C093FF (soft violet)
 */
export const violetPalette = [
  '#C093FF', // Primary Violet
  '#A66FE8', // Darker Violet
  '#D4B0FF', // Light Violet
  '#8B5CF6', // Purple
  '#E9DAFF', // Very Light Violet
  '#7C3AED', // Deep Purple
  '#B794F4', // Medium Violet
  '#9F7AEA', // Soft Purple
  '#DDD6FE', // Pale Violet
  '#6D28D9', // Dark Purple
  '#C4B5FD', // Lavender
  '#A78BFA', // Violet Blue
] as const;

/**
 * Vibrant palette - highly saturated, distinct colors
 * Best for charts with 5-10 different data series
 */
export const vibrantPalette = [
  '#E55555', // Red
  '#3CAC94', // Teal
  '#FF8F63', // Light Salmon
  '#7BC7B1', // Mint
  '#E6CB54', // Yellow
  '#339FB8', // Blue
  '#A97EB6', // Purple
  '#6EB0CF', // Sky Blue
  '#E8A775', // Peach
  '#95D5BE', // Light Green
  '#FFC29E', // Cream
  '#FF9892', // Light Red
] as const;

/**
 * Rainbow palette - full spectrum of colors
 * Best for charts with many different data series
 */
export const rainbowPalette = [
  '#E60000', // Red
  '#E67000', // Orange
  '#EDED00', // Yellow
  '#00D700', // Green
  '#0000D7', // Blue
  '#410070', // Indigo
  '#8500B8', // Violet
  '#E60079', // Deep Pink
  '#00BDB8', // Dark Turquoise
  '#2ABF2A', // Lime Green
  '#E65236', // Tomato
  '#1A7FE6', // Dodger Blue
] as const;

/**
 * Pastel palette - soft, gentle colors
 * Best for less intense visualizations
 */
export const pastelPalette = [
  '#FF9BA5', // Pastel Red
  '#FFB4B3', // Light Red
  '#FFEF9E', // Pastel Yellow
  '#A2D5A2', // Pastel Green
  '#A2AFEA', // Pastel Blue
  '#CCAAD4', // Pastel Purple
  '#FFC7B5', // Pastel Orange
  '#BCD9DC', // Pastel Cyan
  '#DDD4F0', // Pastel Lavender
  '#FFD999', // Pastel Gold
  '#B0D4B0', // Pastel Mint
  '#F0A3BE', // Pastel Pink
] as const;

/**
 * Material Design palette - based on Material Design color system
 * Professional and well-tested color combinations
 */
export const materialPalette = [
  '#D92F1E', // Red
  '#0D7BD9', // Blue
  '#3A8C38', // Green
  '#D98000', // Orange
  '#7A1F8C', // Purple
  '#0098A8', // Cyan
  '#D9C42E', // Yellow
  '#D94311', // Deep Orange
  '#5D3B32', // Brown
  '#006B5E', // Teal
  '#2E39A8', // Indigo
  '#CC0052', // Pink
] as const;

/**
 * Cool palette - blues, teals, and cool tones
 * Best for data visualization with professional appearance
 */
export const coolPalette = [
  '#0159A8', // Deep Blue
  '#0169B8', // Light Blue
  '#007B91', // Cyan
  '#0098AC', // Cyan Dark
  '#004C52', // Teal Dark
  '#005247', // Teal
  '#006876', // Teal Light
  '#0C4DB8', // Blue
  '#0A3A7F', // Deep Blue Dark
  '#0A47C1', // Blue Light
  '#1678D9', // Blue Bright
  '#6EBFE0', // Light Cyan
] as const;

/**
 * Warm palette - reds, oranges, and warm tones
 * Best for heat maps and warm data representations
 */
export const warmPalette = [
  '#B82424', // Red Dark
  '#D92F1E', // Red
  '#CC3F0E', // Red Orange
  '#E65D00', // Orange Dark
  '#E67D00', // Orange
  '#E68C00', // Orange Light
  '#E6B400', // Amber
  '#E6B820', // Yellow Light
  '#E6A040', // Orange Light
  '#E6941F', // Deep Orange
  '#E6603A', // Red Orange Dark
  '#CC2A2A', // Red Light
] as const;

/**
 * Get a color from a palette by index
 * Automatically cycles through palette if index exceeds palette length
 * 
 * @param palette - Color palette array
 * @param index - Index of color (will wrap around if needed)
 * @returns Color string
 */
export function getPaletteColor(
  palette: readonly string[],
  index: number
): string {
  return palette[index % palette.length];
}

/**
 * Get multiple colors from a palette
 * 
 * @param palette - Color palette array
 * @param count - Number of colors needed
 * @returns Array of color strings
 */
export function getPaletteColors(
  palette: readonly string[],
  count: number
): string[] {
  return Array.from({ length: count }, (_, i) => palette[i % palette.length]);
}

/**
 * Chart palettes export - choose your preferred palette
 */
export const chartPalettes = {
  violet: violetPalette,
  vibrant: vibrantPalette,
  rainbow: rainbowPalette,
  pastel: pastelPalette,
  material: materialPalette,
  cool: coolPalette,
  warm: warmPalette,
} as const;

export type ChartPalette = keyof typeof chartPalettes;

/**
 * Default palette - violet theme
 * Use this as the standard palette across all chart components
 */
export const defaultPalette = violetPalette;
